import type { GameContent, GameId, Item, Level, LevelNumber, Band } from './types'

/**
 * Level unlocking and the teacher override.
 *
 * games-spec.md §3: *"Levels unlock **sequentially**, but a teacher may jump a
 * child to any level (T4 band override)."*
 *
 * 🔴 PURE FUNCTIONS, NOT COMPONENT STATE. Which levels a child may enter is a
 * property of their progress, not of whichever screen is mounted — and the
 * level-select screen, the resume flow and a teacher's dashboard all need the
 * same answer.
 */

/**
 * 🔴 RE-EXPORTED, NOT RE-DECLARED — ADR-037.
 *
 * This was a second, independent `1 | 2 | 3` while `types.ts` said
 * `1 | 2 | 3 | 4 | 5`. The two never had to agree, and did not. The union now
 * has one home, next to the `Level` interface it describes; this line exists
 * only so the ~30 modules importing it from here (directly or through the
 * barrel) keep the specifier they already have.
 */
export type { LevelNumber }

/** A level tile's state on the select screen (wireframe B9). */
export type LevelState = 'completed' | 'unlocked' | 'locked'

export interface ProgressInput {
  /** Highest level this child has completed. 0 = none yet. */
  completed: number
  /**
   * A teacher's T4 band override. When set, **every** level is enterable.
   *
   * 🔴 IT WINS, AND THAT IS DELIBERATE. A teacher placing a child at level 3
   * has information the software does not: they know this child, and they may
   * be picking up where another school left off, or matching a sibling's group,
   * or working around a bad day. Software second-guessing that produces a child
   * held back by a machine in front of their class.
   */
  teacherOverride?: boolean
}

export const LEVELS: readonly LevelNumber[] = [1, 2, 3]

/**
 * 🔴 HOW MANY LEVELS A CHILD IS WORKING THROUGH — ADR-037.
 *
 * Derived from `LEVELS`, never written as a digit, because the digit is what
 * drifted: `SkillProgress` drew `[1, 2, 3, 4, 5]` as five segments and printed
 * "Level 3 of 5" on a parent's report, while the level screen offered three
 * tiles and the backend rejected `level: 4`. Nothing was wrong with any one of
 * those; they simply each carried their own copy of the number.
 *
 * ⚠️ THIS IS A DENOMINATOR SHOWN TO A PARENT, so getting it wrong is not a
 * cosmetic bug — "Level 3 of 5" tells a parent their child is 60% of the way
 * through when they have in fact finished.
 */
export const LEVEL_COUNT = LEVELS.length

/**
 * 🔴 GAMES WHERE NO LEVEL IS EVER LOCKED — LETTER-LAGOON-PLAN.md §2.3, D1.
 *
 * *"Levels 2 and 3 unlock by playing, not by scoring. Every child sees all three
 * worlds. No door stays locked."* What differs is the **entry point**, and it is
 * invisible: a child who struggled through L1 enters L2 at step 2.1 rung 6, one
 * who breezed enters at 2.3 rung 0. Same screen, fifteen rungs apart — see
 * `letter-lagoon/ladder/entry.ts`.
 *
 * *"A locked door tells a five-year-old they are not good enough, and no amount
 * of gentle copy undoes that."*
 *
 * ⚠️ ONE GAME, BY DECISION, AND THE COST IS REAL AND ACCEPTED. D1 was settled as
 * Letter Lagoon only: the other four keep ADR-037's gate, so a child can meet
 * two different rules in one session. The alternative was a product-wide change
 * to a shipped ADR, for four games this plan does not otherwise touch.
 *
 * 🔴 IT IS A SET SO THAT WIDENING IT IS A DATA CHANGE, NOT A REFACTOR. Adding
 * the other four ids here is the whole of option (b) — plus its own ADR entry,
 * and a re-read of four games' level tests.
 */
/*
 * ⚠️ SKY QUEST JOINED THE SET SO ALL THREE LEVELS CAN BE PLAYED AND TESTED.
 *
 * The block above states the cost honestly and it still applies: ADR-037's
 * sequential gate now covers three games rather than four, so a child can meet
 * two rules in one session. What it buys is that L2 and L3 are reachable without
 * first completing L1 — which is what they were not, and what makes the three
 * level tiles on the kid screen mean anything before a child has finished one.
 *
 * 🔴 IT IS A DATA CHANGE, WHICH IS WHAT THE NOTE ABOVE PROMISED IT WOULD BE. No
 * caller changes: `levelState` already consults this set, and every caller that
 * knows which game it is asking about already passes `gameId`. Reverting is
 * deleting one string.
 */
/*
 * ⚠️ KALQY WORLD JOINED FOR THE SAME REASON SKY QUEST DID — all three levels
 * playable and testable without first completing L1.
 *
 * 🔴 AND ITS THREE LEVELS ARE THE SAME MECHANIC AT THREE SPEEDS, NOT THREE
 * DIFFICULTIES OF ONE. `WORLDS` in `trailSpec` gives L1/L2/L3 a world each —
 * Sunny Forest at 1x with 5 obstacles, Blossom Valley at 1.15x with 8, Coral
 * Cove at 1.3x with 11 — and the question BAND, not the level, is what changes
 * what is asked. A child gated out of L2 was being gated out of scenery and a
 * speed step, never out of content they had not earned.
 *
 * ⚠️ ADR-037'S SEQUENTIAL GATE NOW COVERS TWO GAMES RATHER THAN THREE, and the
 * cost the original note states still stands: a child can meet two different
 * rules in one session.
 */
/**
 * 🔴 TURO WORLD JOINS THE LIST, AND ITS THREE LEVELS ARE THREE DIFFERENT
 * CURRICULA RATHER THAN THREE DIFFICULTIES — which is the same argument Kalqy
 * World makes above, only stronger.
 *
 * L1 is the morning warm-up and calibration. **L2 is body autonomy and safe
 * touches. L3 is courtesy language.** A sequential gate would mean a child
 * reaches body-safety content by completing enough calisthenics, and is refused
 * it otherwise — which is incoherent in both directions.
 *
 * ⚠️ `TURO_WORLD_L2_L3_PLAN.md` §12.4 SETTLES L3 EXPLICITLY: L3 must not gate
 * behind L2, because *"gating courtesy behind safety content gets L2 skipped by
 * schools that want the easy win first."*
 *
 * 🔴 UNLOCKED IS NOT UNGUARDED, AND L2 IS THE PROOF. Progress no longer decides
 * whether a child may reach it; **an adult does** — `AdultGateScreen` stands in
 * front of it per §9.4, and that is the correct control. Progress gating was
 * never the thing protecting L2; it only looked like it.
 */
/*
 * 🔴 EMPTY — EVERY GAME IS LEVEL AFTER LEVEL AGAIN (ADR-058, product owner,
 * 2026-10-10): level 2 opens once level 1 is passed, level 3 once level 2 is.
 * The notes above record why four games were once opened up; that decision is
 * reversed for all five. A teacher can still open any level for a child
 * (`teacherOverride`), and Turo's World L2 keeps its adult gate in front of it.
 * The set stays so a future exception is a data change, not a refactor.
 */
export const LEVELS_NEVER_LOCKED: ReadonlySet<GameId> = new Set<GameId>([])

export function levelState(
  level: LevelNumber,
  { completed, teacherOverride }: ProgressInput,
  /**
   * Which game is being asked about.
   *
   * ⚠️ OPTIONAL, AND OMITTING IT KEEPS TODAY'S BEHAVIOUR EXACTLY. Every existing
   * caller — `session/builder.ts`, the four other games, the level screen for
   * them — compiles and behaves unchanged. Only a caller that names Letter
   * Lagoon gets the new rule, which is what makes D1's scope reversible.
   */
  gameId?: GameId,
): LevelState {
  if (level <= completed) return 'completed'
  // Sequential: the next one after the last completed. `completed: 0` unlocks
  // level 1, so a child with no history can always start.
  if (teacherOverride || level === completed + 1) return 'unlocked'
  /*
   * 🔴 CHECKED AFTER `completed`, SO THE TICK SURVIVES. A level the child has
   * finished still reads 'completed' rather than 'unlocked' — that mark is a
   * record of what they did, and flattening it to a neutral state would remove
   * the one thing on that screen which is unambiguously theirs.
   */
  if (gameId !== undefined && LEVELS_NEVER_LOCKED.has(gameId)) return 'unlocked'
  return 'locked'
}

export function isLevelPlayable(
  level: LevelNumber,
  progress: ProgressInput,
  gameId?: GameId,
): boolean {
  return levelState(level, progress, gameId) !== 'locked'
}

/**
 * 🔴 THERE IS NO 'failed' STATE, AND THERE MUST NEVER BE ONE.
 *
 * A locked level is **not yet**, never *failed* (wireframe B9: "no failure
 * state"). The distinction is the whole difference between a child who has more
 * to look forward to and a child who has been marked wrong — and at 3 to 6, the
 * second one stops playing.
 *
 * Exported so the intent is greppable rather than implicit in an absence.
 */
export const LEVEL_STATES: readonly LevelState[] = ['completed', 'unlocked', 'locked']

/**
 * Picks the items for one round.
 *
 * 🔴 SAMPLED, NOT SLICED. The pool is ≥ 2× `itemCount` (§3.2) precisely so a
 * child repeating a level does not get the same six items in the same order —
 * which teaches the sequence rather than the number.
 *
 * `pick` is injected rather than calling Math.random, so a round is
 * reproducible in a test and a replay.
 */
/**
 * What a child actually hears, as a key.
 *
 * 🔴 THE ENGLISH PROMPT, NOT THE `audioKey`. Two items can share a recording and
 * still be different questions, and — more to the point here — two items with
 * different recordings can carry identical words. What a child recognises as
 * "the same question again" is the sentence, so the sentence is the key.
 */
const spokenKey = (item: Item): string => item.prompt.en.trim().toLowerCase()

export function sampleRound(
  level: Level,
  pick: (n: number) => number,
  band?: Band,
  /** Item ids from the last round — preferred against, so a replay is not a rerun. */
  avoid?: ReadonlySet<string>,
): Item[] {
  /*
   * ─── 🔴 BAND SELECTS WHICH ITEMS INSIDE A LEVEL A CHILD SEES ──────────────
   *
   * Level and band are two axes, not one (PLAN §D1). LEVEL is what advances and
   * gates — for Kalqy World it is the WORLD, and it carries the motor
   * progression. BAND comes from the child's profile and carries the
   * developmental one. A three-year-old who finishes World 1 unlocks World 2
   * and still meets colour and animal-sound questions; a six-year-old starting
   * fresh plays World 1 with rhymes and addition.
   *
   * ⚠️ BINDING THE TWO WOULD PUNISH EXACTLY THE WRONG CHILDREN. If harder
   * questions only arrived with a faster world, the only route to them would be
   * surviving a motor setting the children who most need the easier one cannot
   * hold.
   *
   * ⚠️ IT IS A PREFERENCE, NOT A FILTER — the same shape as the spoken-line rule
   * below, and for the same reason. A level whose band has too few items serves
   * a SHORT ROUND if this filters outright, and a short round reads to a child
   * as the game ending early. Falling back to the whole pool gives them a full
   * round of slightly-off-band questions, which is the better failure.
   *
   * ⚠️ AN ITEM WITH NO BAND IS ALWAYS ELIGIBLE. `Item.band` is optional and most
   * of the library predates it; treating absent as "wrong band" would empty
   * three games' pools the moment a band was passed.
   */
  const inBand = band
    ? level.items.filter((it) => it.band === undefined || it.band === band)
    : level.items
  const pool = [...(inBand.length >= level.itemCount ? inBand : level.items)]
  const chosen: Item[] = []
  const heard = new Set<string>()
  const answered = new Set<string>()
  const n = Math.min(level.itemCount, pool.length)

  for (let i = 0; i < n; i++) {
    /*
     * ─── 🔴 NO ROUND ASKS THE SAME SENTENCE TWICE ───────────────────────────
     *
     * De-duplicating by item ID is not enough, and it looks like it is. A level's
     * table can hold several distinct items a child HEARS as one question — two
     * rows both prompted *"Which cloud has two stars?"* differ in their options
     * and their id, so the sampler counted two questions while the child was
     * asked the same thing twice in one sitting.
     *
     * ⚠️ AND IT LANDS WORST RIGHT AFTER A CORRECT ANSWER, which is where it was
     * noticed. A child who has just got something right and is immediately asked
     * it again reads the game as not having noticed them — and the report card
     * gains a second correct answer to an item already measured, inflating the
     * module's accuracy on no new evidence.
     *
     * ⚠️ A PREFERENCE, NOT A FILTER, AND THE FALLBACK IS THE POINT. Some content
     * is legitimately one sentence across many items — Letter Lagoon's *"odd one
     * out"* module is eight questions with one prompt, where the OPTIONS are the
     * question. Refusing outright would leave a round short; preferring an
     * unheard line takes the duplicate only when there is nothing else to take.
     */
    /*
     * ⚠️ AND NO ROUND ASKS FOR THE SAME ANSWER TWICE, WHILE IT CAN AVOID IT.
     * Different sentences can still be one question: *"Where is the ball?"* and
     * *"Where is the red ball?"* point at the same card, and *"3 apples and 2
     * more"* is *"2 shells and 3 more"* to a child. The same preference, one
     * step stricter — an unused answer first, then any unheard line, then
     * anything.
     */
    /*
     * ⚠️ AND NOT LAST ROUND'S QUESTIONS, WHILE IT CAN AVOID THEM — ranked above
     * the answer rule. A replayed question is what a child notices; two
     * different questions sharing an answer (Bridge L2 has 9 answers across 14
     * patterns) is not. Answer-first left about one replay in fifteen re-asking
     * a pattern the child had just done.
     */
    const fresh = pool.filter((it) => !heard.has(spokenKey(it)))
    const unused = fresh.filter((it) => !answered.has(String(it.target)))
    const rested = (xs: Item[]) => (avoid ? xs.filter((it) => !avoid.has(it.id)) : xs)
    const from = [rested(unused), rested(fresh), unused, fresh].find((xs) => xs.length > 0) ?? pool
    const taken = from[pick(from.length)] ?? from[0]
    pool.splice(pool.indexOf(taken), 1)
    heard.add(spokenKey(taken))
    answered.add(String(taken.target))
    chosen.push(taken)
  }
  return chosen.map((item) => shuffleChoices(item, pick))
}

/**
 * Puts the answer somewhere other than first.
 *
 * 🔴 EVERY ITEM IN EVERY GAME WAS AUTHORED WITH THE TARGET AT `choices[0]`.
 * Not most of them — all of them, in every content table:
 *
 *     item('tw.l1.a', 'Which one do we use to eat?', …, 'spoon', ['spoon', 'comb', 'shoe'])
 *     item('ll.l1.a', 'Step on the letter m',        …, 'm',     ['m', 'a', 's'])
 *
 * It is the natural way to write a question — you type the answer, then some
 * distractors — and nothing downstream ever reordered it. So the leftmost
 * choice was ALWAYS the right one, in every round of every game.
 *
 * 🔴 WHAT THAT COSTS IS THE ENTIRE MEASUREMENT, WHICH IS WORSE THAN IT COSTING
 * THE GAME. A child who notices — and they notice fast, because it is the only
 * rule in the room that never breaks — scores 100% without reading a letter or
 * recognising a picture. Every ANSWER_CORRECT after that is a lie: the mastery
 * ladder promotes them, the report card tells a teacher they know their sounds,
 * and the one thing this product exists to measure is the one thing it can no
 * longer see. It also teaches something false and sticky at this age: *the
 * answer is the one on the left*.
 *
 * ⚠️ IT SHUFFLES `choices` AND LEAVES `target` ALONE, which is why no consumer
 * has to change. Every game derives its answer as `choices.indexOf(target)` —
 * `TuroRound`, `BridgeRound`, Letter Lagoon's `sequence` mapping — so moving
 * the array moves the correct index with it, everywhere, for free. Anything
 * that had hardcoded `0` was already wrong.
 *
 * 🔴 `pick` IS THE INJECTED PICKER `sampleRound` ALREADY TAKES, not
 * `Math.random`. A round has to stay reproducible in a test and in a replay,
 * and reaching for a global RNG here would have made every existing spec that
 * passes `() => 0` non-deterministic.
 *
 * ⚠️ SEQUENCE ITEMS ARE SHUFFLED TOO, deliberately. Letter Lagoon's blending
 * items ask a child to walk `c`, then `a`, then `t`, and `sequence` names those
 * letters by VALUE — `sequence.map(l => stones.indexOf(l))` — so the walk still
 * resolves against a reordered floor. Leaving those stones in word order would
 * let a child spell `cat` by walking left to right without reading any of it,
 * which is the same hole one level down.
 */
export function shuffleChoices(item: Item, pick: (n: number) => number): Item {
  if (item.choices.length < 2) return item

  /*
   * 🔴 A COPY, BECAUSE THE LEVEL TABLES ARE MODULE-LEVEL CONSTANTS. `pool`
   * above holds the ORIGINAL item objects — shuffling `item.choices` in place
   * would permanently reorder the shared content for the rest of the session,
   * and the next round of that level would inherit it.
   */
  const choices = [...item.choices]
  for (let i = choices.length - 1; i > 0; i--) {
    const j = pick(i + 1)
    const tmp = choices[i]
    choices[i] = choices[j]
    choices[j] = tmp
  }
  return { ...item, choices }
}

/** A uniform picker. Separate so `sampleRound` stays deterministic in tests. */
export const randomPick = (n: number): number => Math.floor(Math.random() * n)

export function getLevel(content: GameContent, level: LevelNumber): Level {
  // The tuple type guarantees three entries in order and `LevelNumber` is
  // 1|2|3, so the index is always in range — ADR-037. (Said "five" until HH-4.)
  return content.levels[level - 1]
}
